import React, { useState, useEffect, useRef } from "react";
import { PuffLoader } from "react-spinners";
import { useLocation } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { verifyRecaptcha, searchRiderById, getIpAddress } from "./Axios/apiService";
import getReadableLocation from "./Helpers/getReadablelocation";
import { supabase, supabaseConfigMissing } from "./supabaseClient";
import {
  lookupSafetyIdByCode,
  buildRiderInfoFromSupabase,
} from "./Helpers/lookupSafetyIdSupabase";
import {
  lookupSafetyIdByApi,
  getEmsApiBase,
  validateEmsPin,
  fetchEmsDocuments,
  fetchEmsNotes,
  getDocumentViewUrl,
  fetchEmsDocumentViewUrl,
} from "./Helpers/lookupSafetyIdApi";
import DocumentPreview, { getPreviewType } from "./DocumentPreview";
import { SUPPORTED_LANGUAGES, getActiveLanguage, setAppLanguage } from "./Helpers/langHelper";


const EMSAccess = () => {
  const [currentLanguage, setCurrentLanguage] = useState(getActiveLanguage());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setCurrentLanguage(lang);
    setAppLanguage(lang);
    if (riderId) sessionStorage.setItem("pendingRiderId", riderId);
    window.location.reload();
  };

  const [riderId, setRiderId] = useState("");
  const [riderInfo, setRiderInfo] = useState(null);
  const [error, setError] = useState("");
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  // What3Words: viewer's current location (get at view time, not stored)
  const [locationW3w, setLocationW3w] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState("");
  // PIN and Docs/Notes: popup for PIN; full-screen for Docs/Notes lists and Note detail
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinModalType, setPinModalType] = useState("documents"); // "documents" | "notes"
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinSubmitting, setPinSubmitting] = useState(false);
  const [docsToken, setDocsToken] = useState(null);
  const [notesToken, setNotesToken] = useState(null);
  const [documentsList, setDocumentsList] = useState([]);
  const [notesList, setNotesList] = useState([]);
  const [docsNotesLoading, setDocsNotesLoading] = useState(false);
  const [docsNotesError, setDocsNotesError] = useState("");
  const [viewMode, setViewMode] = useState(null); // null | "docs-notes" (tabbed view)
  const [activeTab, setActiveTab] = useState("documents"); // "documents" | "notes"
  const [selectedNote, setSelectedNote] = useState(null);
  const [docViewerDoc, setDocViewerDoc] = useState(null); // doc object when viewing in-app
  const [documentViewLoading, setDocumentViewLoading] = useState(null); // doc we're fetching view URL for
  const viewUrlEndpointUnavailableRef = useRef(false); // set when view-url returns 404 to avoid repeated calls

  const location = useLocation();
  // Failed attempts this session only — captcha shows after 3 in this page load, not from previous sessions
  const [failedAttemptsThisSession, setFailedAttemptsThisSession] = useState(0);

  useEffect(() => {
    const lastAttemptTime = localStorage.getItem("lastAttemptTime");
    const currentTime = new Date().getTime();
    // Reset persisted count if more than 2 hours have passed (for any future use)
    if (lastAttemptTime && currentTime - lastAttemptTime > 7200000) {
      localStorage.setItem("failedAttempts", "0");
    }
    // Do NOT restore captcha or failed count from localStorage — each page load starts at 0
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const riderIdFromUrl = urlParams.get('ID') || urlParams.get('id') || urlParams.get('safety_id');
    if (riderIdFromUrl) {
      setRiderId(riderIdFromUrl);
      handleRiderIdSubmit(null, riderIdFromUrl);
      return;
    }
    const savedId = sessionStorage.getItem("pendingRiderId");
    if (savedId) {
      sessionStorage.removeItem("pendingRiderId");
      setRiderId(savedId);
      handleRiderIdSubmit(null, savedId);
    }
  }, [location.search]);

  const handleCaptchaChange = async (token) => {
    if (token) {
      try {
        setLoading(true);
        const captchaResponse = await verifyRecaptcha(token);
        if (captchaResponse.success) {
          setCaptchaVerified(true);
          setCaptchaToken(token);
          setShowCaptcha(false);
          setFailedAttemptsThisSession(0);
          localStorage.setItem("failedAttempts", "0");
        } else {
          setError("CAPTCHA verification failed.");
          setCaptchaVerified(false);
        }
      } catch (error) {
        console.error("Error verifying CAPTCHA:", error);
        setError("CAPTCHA verification failed.");
        setCaptchaVerified(false);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (error && window.grecaptcha) {
      try {
        window.grecaptcha.reset();
      } catch (e) {
        console.error("Error resetting reCAPTCHA:", e);
      }
    }
  }, [error]);

  // Parse date-only string (e.g. YYYY-MM-DD) as local date to avoid UTC-off-by-one-day display
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const s = String(dateString).trim();
    const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    const date = new Date(s);
    return isNaN(date.getTime()) ? null : date;
  };

  // Function to calculate age based on DOB
  const calculateAge = (dob) => {
    const birthDate = parseLocalDate(dob) || new Date(dob);
    if (isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";
    const cleaned = ("" + phoneNumber).replace(/\D/g, ""); // Remove all non-digit characters
    if (cleaned.length !== 10) return phoneNumber; // Return the original value if length is not 10
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]})-${match[2]}-${match[3]}` : phoneNumber;
  };

  // Sort contacts: user PIN knower first, company PIN knower second, then all others
  const sortContactsPinKnowersFirst = (contacts) => {
    if (!Array.isArray(contacts)) return [];
    return [...contacts].sort((a, b) => {
      const order = (c) => (c.knowsUserPin ? 0 : c.knowsCompanyPin ? 1 : 2);
      return order(a) - order(b);
    });
  };

  // What3Words: get viewer's location and convert to 3-word address (per docs/ems-access-update-spec.md)
  const getLocationAndConvert = () => {
    const apiKey = process.env.REACT_APP_WHAT3WORDS_API_KEY;
    if (!apiKey || !apiKey.trim()) {
      setLocationError("What3Words API key not configured.");
      return;
    }
    const isSecureContext = typeof window !== "undefined" && (window.isSecureContext || window.location?.protocol === "https:");
    setLocationError("");
    setLocationW3w(null);
    setLocationLoading(true);

    const onPosition = async (position) => {
      const { latitude, longitude } = position.coords;
      try {
        const params = new URLSearchParams({
          coordinates: `${latitude},${longitude}`,
          language: "en",
          format: "json",
        });
        const res = await fetch(`https://api.what3words.com/v3/convert-to-3wa?${params}`, {
          headers: { "X-Api-Key": apiKey.trim() },
        });
        const data = await res.json();
        if (!res.ok) {
          if (process.env.NODE_ENV === "development") {
            console.warn("[What3Words] API error response:", res.status, data?.error || data);
          }
          const msg = data?.error?.message || data?.message || `HTTP ${res.status}`;
          const isQuotaOrPlan = /quota|plan|access to this feature/i.test(msg);
          setLocationError(
            isQuotaOrPlan
              ? "Location service is temporarily unavailable. Please try again later."
              : msg
          );
          return;
        }
        const words = data?.words;
        if (typeof words === "string" && words.trim()) {
          setLocationW3w("///" + words.trim().toLowerCase().replace(/\s+/g, "."));
        } else {
          setLocationError("Could not get 3-word address.");
        }
      } catch (err) {
        setLocationError(err?.message || "Failed to get location address.");
      } finally {
        setLocationLoading(false);
      }
    };

    const onGeoError = (err) => {
      setLocationLoading(false);
      if (err?.code === 1) {
        setLocationError(
          isSecureContext
            ? "Location permission denied. Allow location for this site in your browser or device settings, then try again."
            : "Location needs a secure connection (HTTPS). On mobile, open this page over HTTPS to use Get Location, or use Get Location on a computer."
        );
      } else if (err?.code === 2) {
        setLocationError("Location unavailable.");
      } else if (err?.code === 3) {
        setLocationError("Location request timed out.");
      } else {
        setLocationError(err?.message || "Could not get your location.");
      }
    };

    if (!navigator.geolocation) {
      setLocationLoading(false);
      setLocationError("Geolocation is not supported by this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(onPosition, onGeoError, {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    });
  };

  // Function to handle Rider ID submission
  const handleRiderIdSubmit = async (e, riderIdFromUrl) => {
    if (e !== null) e.preventDefault();

    // Check if CAPTCHA is required but not verified (only when site key is configured)
    if (showCaptcha && process.env.REACT_APP_CAPTCHA_SITE_KEY && !captchaVerified) {
      setError("Please complete the CAPTCHA verification.");
      return;
    }

    setError("");
    setRiderInfo(null);
    setShowAdditionalInfo(false);
    setLocationW3w(null);
    setLocationError("");
    setLoading(true);

    try {
      const riderIdToUse = riderId || riderIdFromUrl;
      const upperCasedRiderId = riderIdToUse.toUpperCase();

      // When backend EMS API URL is set (e.g. REACT_APP_SUPABASE_LOCALHOSTAPI_URL for localhost), use it first.
      // getEmsApiBase() uses current host when env has localhost so the app works from phone (same Wi‑Fi).
      const emsApiUrl = getEmsApiBase();
      if (emsApiUrl) {
        console.info("[EMS] Lookup via API:", emsApiUrl, "safetyId:", upperCasedRiderId);
        const apiResult = await lookupSafetyIdByApi(upperCasedRiderId);
        if (apiResult?.apiError) {
          const err = apiResult.apiError;
          const status = apiResult.status;
          const backendMessage = apiResult.message;
          if (err === "wristband" && apiResult.redirectUrl) {
            // Unclaimed → register it; expired → the coverage-ended page.
            window.location.replace(apiResult.redirectUrl);
            return;
          }
          if (err === "network") {
            setError("Could not reach the server. Ensure the backend is running and that the API port (e.g. 8443) is allowed in the firewall.");
          } else if (err === "parse" || err === "cors") {
            setError("Server blocked the request (CORS). On mobile the backend must allow origin: " + (typeof window !== "undefined" ? window.location.origin : "this page"));
          } else if (err === "http" && status === 401) {
            setError(backendMessage || "Server rejected the request (check API key).");
          } else if (err === "http" && status === 404) {
            setError(backendMessage || "Safety ID does not exist.");
          } else if (err === "http" && status) {
            setError(backendMessage || "Server error (status " + status + ").");
          } else {
            setError("Safety ID does not exist.");
          }
          setLoading(false);
          return;
        }
        if (apiResult?.riderInfo) {
          setFailedAttemptsThisSession(0);
          setShowCaptcha(false);
          setCaptchaVerified(false);
          if (process.env.REACT_APP_BACKEND_URL) {
            try {
              const userIp = await getIpAddress();
              const uid = apiResult.userId || null;
              const u = apiResult.riderInfo;
              const userName = u.fullName || [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || null;
              navigator.geolocation.getCurrentPosition(
                async (position) => {
                  try {
                    const { latitude, longitude } = position.coords;
                    const locationName = await getReadableLocation(latitude, longitude);
                    await searchRiderById(upperCasedRiderId, uid, userIp, locationName, null, null, userName);
                  } catch (e) {
                    console.error("Failed to log search event:", e);
                  }
                },
                () => {
                  searchRiderById(upperCasedRiderId, uid, userIp, null, null, null, userName).catch((e) =>
                    console.error("Failed to log search event:", e)
                  );
                }
              );
            } catch (logError) {
              console.error("Failed to log search event:", logError);
            }
          }
          console.log("[EMS] riderInfo from API:", apiResult.riderInfo);
          console.log("[EMS] show flags:", {
            showDobToEMS: apiResult.riderInfo.showDobToEMS,
            showGenderToEMS: apiResult.riderInfo.showGenderToEMS,
            showHeightToEMS: apiResult.riderInfo.showHeightToEMS,
            showWeightToEMS: apiResult.riderInfo.showWeightToEMS,
            showNotesToEMSToEMS: apiResult.riderInfo.showNotesToEMSToEMS,
            heightFeet: apiResult.riderInfo.heightFeet,
            heightInches: apiResult.riderInfo.heightInches,
            weight: apiResult.riderInfo.weight,
            notesToEMS: apiResult.riderInfo.notesToEMS,
            gender: apiResult.riderInfo.gender,
            age: apiResult.riderInfo.age,
          });
          setRiderInfo(apiResult.riderInfo);
          setLoading(false);
          return;
        }
        console.warn("[EMS] API returned no riderInfo for:", upperCasedRiderId);
      }

      // Supabase (direct) only when EMS API URL is NOT set — when using localhost/dev API we don't fall back (avoids CORS)
      if (!emsApiUrl) {
        console.info("[EMS] Lookup via Supabase, safetyId:", upperCasedRiderId);
        const supabasePayload = await lookupSafetyIdByCode(supabase, upperCasedRiderId);
        if (supabasePayload) {
          if (!supabasePayload.hasActiveSubscription) {
            setError("The user is not subscribed.");
            setLoading(false);
            return;
          }
          const riderInfoFromSupabase = buildRiderInfoFromSupabase(
            supabase,
            supabasePayload,
            calculateAge
          );
          if (riderInfoFromSupabase) {
            setFailedAttemptsThisSession(0);
            setShowCaptcha(false);
            setCaptchaVerified(false);
            if (process.env.REACT_APP_BACKEND_URL) {
              try {
                const userIp = await getIpAddress();
                const u = supabasePayload.user;
                const userName = [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || null;
                navigator.geolocation.getCurrentPosition(
                  async (position) => {
                    try {
                      const { latitude, longitude } = position.coords;
                      const locationName = await getReadableLocation(latitude, longitude);
                      await searchRiderById(
                        upperCasedRiderId,
                        u.id,
                        userIp,
                        locationName,
                        u.email,
                        u.phone_number,
                        userName
                      );
                    } catch (e) {
                      console.error("Failed to log search event:", e);
                    }
                  },
                  () => {
                    searchRiderById(upperCasedRiderId, u.id, userIp, null, u.email, u.phone_number, userName).catch((e) =>
                      console.error("Failed to log search event:", e)
                    );
                  }
                );
              } catch (logError) {
                console.error("Failed to log search event:", logError);
              }
            }
            setRiderInfo(riderInfoFromSupabase);
            setLoading(false);
            return;
          }
          console.warn("[EMS] Supabase returned payload but no riderInfo (no subscription or missing data) for:", upperCasedRiderId);
        } else {
          console.warn("[EMS] Supabase returned no payload for:", upperCasedRiderId);
        }
      } else {
        console.info("[EMS] Using EMS API only (no Supabase fallback).");
      }

      // Not found (API returned nothing, or Supabase returned nothing when no API URL)
      const nextFailed = failedAttemptsThisSession + 1;
      console.warn("[EMS] Safety ID not found. Failed attempts this session:", failedAttemptsThisSession, "→", nextFailed, "(captcha after 3)");
      setFailedAttemptsThisSession(nextFailed);
      localStorage.setItem("lastAttemptTime", String(new Date().getTime()));
      if (nextFailed >= 3) {
        setShowCaptcha(true);
      }
      if (process.env.REACT_APP_BACKEND_URL) {
        try {
          const userIp = await getIpAddress();
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              try {
                const { latitude, longitude } = position.coords;
                const locationName = await getReadableLocation(latitude, longitude);
                await searchRiderById(upperCasedRiderId, null, userIp, locationName, null, null, null, false);
              } catch (e) {
                console.error("Failed to log failed search event:", e);
              }
            },
            async () => {
              try {
                await searchRiderById(upperCasedRiderId, null, userIp, null, null, null, null, false);
              } catch (e) {
                console.error("Failed to log failed search event:", e);
              }
            }
          );
        } catch (logError) {
          console.error("Failed to log failed search event:", logError);
        }
      }
      setError("Safety ID does not exist.");
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err?.message?.includes("Failed to fetch") || err?.message?.includes("NetworkError") ? "Unable to reach server. Check your connection or CORS settings." : "Error fetching data.");
    } finally {
      setLoading(false); // Stop loading spinner
    }
  };

  const safetyIdForApi = (riderId || "").trim().toUpperCase();

  const openDocsNotes = async (initialTab) => {
    const needsDocs = riderInfo?.hasDocuments && !docsToken;
    const needsNotes = riderInfo?.hasNotes && !notesToken;
    if (!safetyIdForApi || (!riderInfo?.hasDocuments && !riderInfo?.hasNotes)) return;
    setDocsNotesError("");
    if (needsDocs || needsNotes) {
      setPinModalType(initialTab);
      setPinValue("");
      setPinError("");
      setPinModalOpen(true);
      return;
    }
    setDocsNotesLoading(true);
    const [docsResult, notesResult] = await Promise.all([
      riderInfo.hasDocuments && docsToken ? fetchEmsDocuments(safetyIdForApi, docsToken) : { documents: [] },
      riderInfo.hasNotes && notesToken ? fetchEmsNotes(safetyIdForApi, notesToken) : { notes: [] },
    ]);
    setDocsNotesLoading(false);
    if (docsResult.apiError === "forbidden") setDocsToken(null);
    if (notesResult.apiError === "forbidden") setNotesToken(null);
    if (docsResult.apiError === "forbidden" || notesResult.apiError === "forbidden") {
      setPinModalType(initialTab);
      setPinValue("");
      setPinError("");
      setPinModalOpen(true);
      return;
    }
    if (docsResult.apiError && riderInfo.hasDocuments) setDocsNotesError("Could not load documents.");
    if (notesResult.apiError && riderInfo.hasNotes) setDocsNotesError("Could not load notes.");
    setDocumentsList(docsResult.documents ?? []);
    setNotesList(notesResult.notes ?? []);
    setSelectedNote(null);
    setDocViewerDoc(null);
    setActiveTab(initialTab);
    setViewMode("docs-notes");
  };

  const openDocs = () => openDocsNotes("documents");
  const openNotes = () => openDocsNotes("notes");

  const handlePinSubmit = async () => {
    if (!safetyIdForApi) return;
    const pin = (pinValue || "").trim();
    if (!pin) {
      setPinError("PIN is required.");
      return;
    }
    setPinError("");
    setPinSubmitting(true);
    const initialTab = pinModalType;
    const resultDocs = await validateEmsPin(safetyIdForApi, pin, "documents");
    const resultNotes = await validateEmsPin(safetyIdForApi, pin, "notes");
    setPinSubmitting(false);
    if (!resultDocs.valid && !resultNotes.valid) {
      const msg = resultDocs.message || resultNotes.message;
      const reason = resultDocs.reason || resultNotes.reason;
      const isCompanyPinNotAllowed =
        reason === "company_pin_not_allowed" ||
        (msg && /company\s*pin|user'?s\s*pin/i.test(msg));
      if (isCompanyPinNotAllowed) {
        setPinError("The User does not allow the Company PIN - use the User's PIN instead.");
        setPinValue("");
        return;
      }
      if (resultDocs.status === 422 && msg) setPinError(msg);
      else if (resultNotes.status === 422 && msg) setPinError(msg);
      else setPinError("Incorrect PIN. Try again.");
      return;
    }
    if (resultDocs.valid && resultDocs.token) setDocsToken(resultDocs.token);
    if (resultNotes.valid && resultNotes.token) setNotesToken(resultNotes.token);
    if (riderInfo?.hasNotes && !resultNotes.valid && !resultNotes.token) {
      console.warn("[EMS] Notes expected but validate-pin returned no notes token. Backend should return valid: true and token for type \"notes\" when company master PIN is used.");
    }
    setPinModalOpen(false);
    setPinValue("");
    setDocsNotesLoading(true);
    const docsTokenToUse = resultDocs.token || docsToken;
    const notesTokenToUse = resultNotes.token || notesToken;
    const [fetchDocs, fetchNotes] = await Promise.all([
      docsTokenToUse ? fetchEmsDocuments(safetyIdForApi, docsTokenToUse) : { documents: [] },
      notesTokenToUse ? fetchEmsNotes(safetyIdForApi, notesTokenToUse) : { notes: [] },
    ]);
    setDocsNotesLoading(false);
    if (fetchDocs.apiError && riderInfo?.hasDocuments) setDocsNotesError("Could not load documents.");
    if (fetchNotes.apiError && riderInfo?.hasNotes) setDocsNotesError("Could not load notes.");
    setDocumentsList(fetchDocs.documents ?? []);
    setNotesList(fetchNotes.notes ?? []);
    setSelectedNote(null);
    setDocViewerDoc(null);
    setActiveTab(initialTab);
    setViewMode("docs-notes");
  };

  const closePinModal = () => {
    setPinModalOpen(false);
    setPinValue("");
    setPinError("");
  };

  const closeDocsNotesView = () => {
    setViewMode(null);
    setSelectedNote(null);
    setDocViewerDoc(null);
    setDocumentViewLoading(null);
    viewUrlEndpointUnavailableRef.current = false;
  };

  const apiBase = getEmsApiBase();

  const openDocumentViewer = async (doc) => {
    setDocsNotesError("");
    const url = getDocumentViewUrl(doc, apiBase);
    if (url) {
      setDocViewerDoc(doc);
      return;
    }
    if (viewUrlEndpointUnavailableRef.current) {
      setDocsNotesError("View not available. The server does not provide document view URLs.");
      return;
    }
    if (doc.id && docsToken && safetyIdForApi) {
      if (process.env.NODE_ENV === "development") {
        console.info("[EMS doc view] No URL on document. Keys from backend:", doc ? Object.keys(doc) : []);
      }
      setDocumentViewLoading(doc);
      const { viewUrl, notFound } = await fetchEmsDocumentViewUrl(safetyIdForApi, doc.id, docsToken);
      setDocumentViewLoading(null);
      if (notFound) viewUrlEndpointUnavailableRef.current = true;
      if (viewUrl) setDocViewerDoc({ ...doc, viewUrl });
      else setDocsNotesError(notFound ? "View not available. The server does not provide document view URLs." : "Could not load document view.");
    } else {
      setDocsNotesError("View not available for this document.");
    }
  };

  const formatContentType = (contentType) => {
    if (!contentType) return "File";
    const ct = contentType.toLowerCase();
    if (ct.includes("pdf")) return "PDF";
    if (ct.includes("png")) return "PNG";
    if (ct.includes("jpeg") || ct.includes("jpg")) return "JPEG";
    if (ct.includes("gif")) return "GIF";
    if (ct.includes("bmp")) return "BMP";
    if (ct.includes("csv")) return "CSV";
    if (ct.includes("spreadsheetml") || ct.includes("xlsx")) return "Excel";
    if (ct.includes("wordprocessingml") || ct.includes("docx")) return "Word";
    if (ct.includes("mp4")) return "MP4";
    if (ct.includes("webm")) return "WebM";
    if (ct.includes("mpeg") || ct.includes("mp3")) return "MP3";
    if (ct.includes("image/")) return "Image";
    if (ct.includes("video/")) return "Video";
    if (ct.includes("audio/")) return "Audio";
    return "File";
  };

  // Display DOB as MM/DD/YYYY per EMS Access spec (parse as local date to avoid UTC off-by-one)
  const formatDate = (dateString) => {
    const date = parseLocalDate(dateString) || new Date(dateString);
    if (!date || isNaN(date.getTime())) return dateString || "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const buildDetailedSmsBody = () => {
    const words = (locationW3w || "").replace(/^\/+/, "").trim();
    const mapUrl = words ? `https://w3w.co/${words}` : null;
    const lines = [];
    lines.push("Name: " + (riderInfo?.fullName || "Unknown"));
    if (riderInfo?.showDobToEMS && (riderInfo.age != null || riderInfo.dob)) {
      const age = riderInfo.age != null ? riderInfo.age : (riderInfo.dob ? calculateAge(riderInfo.dob) : null);
      if (age != null) lines.push("Age: " + age);
    }
    if (riderInfo?.showGenderToEMS && riderInfo.gender) {
      const g = String(riderInfo.gender).trim();
      lines.push("Gender: " + (g.charAt(0).toUpperCase() + g.slice(1).toLowerCase()));
    }
    if (riderInfo?.showHeightToEMS && (riderInfo.heightFeet != null || riderInfo.heightInches != null)) {
      const h = [riderInfo.heightFeet ? `${riderInfo.heightFeet} ft` : null, riderInfo.heightInches != null ? `${riderInfo.heightInches} in` : null].filter(Boolean).join(" ");
      if (h) lines.push("Height: " + h);
    }
    if (riderInfo?.showWeightToEMS && riderInfo.weight != null && riderInfo.weight !== "") lines.push("Weight: " + riderInfo.weight + " lbs");
    if (riderInfo?.showNotesToEMSToEMS && riderInfo.notesToEMS && String(riderInfo.notesToEMS).trim()) {
      lines.push("");
      lines.push("Message to EMS: " + String(riderInfo.notesToEMS).trim());
    }
    const contactsToSend = (riderInfo?.emergencyContacts || []).filter((c) => c.showToEMS !== false);
    if (contactsToSend.length > 0) {
      lines.push("");
      lines.push("Emergency Contacts");
      contactsToSend.forEach((c) => {
        const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || "—";
        const rel = c.relationship ? ` (${c.relationship})` : "";
        const phone = c.phone ? formatPhoneNumber(c.phone) : "";
        lines.push(`${name}${rel}${phone ? ": " + phone : ""}`);
      });
    }
    if (mapUrl) {
      lines.push("");
      lines.push("Location (click to open map): " + mapUrl);
    }
    const emsLink = riderInfo?.emsUrl || riderInfo?.qrCodeUrl || `https://scanifhurt.com/?ID=${encodeURIComponent(riderId || "")}`;
    lines.push("");
    lines.push("View full profile: " + emsLink);
    lines.push("");
    lines.push("Scan If Hurt - https://www.scanifhurt.com");
    return lines.join("\n");
  };

  const buildContactSmsBody = () => {
    const words = (locationW3w || "").replace(/^\/+/, "").trim();
    const mapUrl = words ? `https://w3w.co/${words}` : null;
    const name = riderInfo?.fullName || "Someone";
    const parts = [`${name} may need assistance.`];
    if (mapUrl) parts.push(`${riderInfo?.firstName || (riderInfo?.fullName || "").split(" ")[0] || "They"} is located here: ${mapUrl}`);
    parts.push("— Scan If Hurt");
    return parts.join(" ");
  };

  const sendSms = (phone, body) => {
    const to = phone ? `sms:${phone}` : "sms:";
    window.open(`${to}?body=${encodeURIComponent(body)}`, "_blank");
  };

  return (
    <div className="overflow-x-hidden">
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <PuffLoader color="#14943E" size={100} />
        </div>
      )}
      {docsNotesLoading && (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1001]"
          aria-busy="true"
        >
          <PuffLoader color="#fff" size={60} />
        </div>
      )}
      {pinModalOpen && (
        <div
          className="fixed inset-0 z-[1002] flex items-center justify-center p-4 bg-black/50"
          onClick={(e) => e.target === e.currentTarget && closePinModal()}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              Enter PIN to view Documents and Notes
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              PIN is set by the account holder.
            </p>
            {riderInfo?.emergencyContacts?.some((c) => c.knowsUserPin || c.knowsCompanyPin) && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-semibold text-amber-900 mb-1">Need the PIN? Contact:</p>
                <ul className="text-sm text-amber-800 space-y-1">
                  {sortContactsPinKnowersFirst(
                    riderInfo.emergencyContacts.filter((c) => c.knowsUserPin || c.knowsCompanyPin)
                  ).map((contact, idx) => (
                      <li key={idx}>
                        {contact.phone ? (
                          <a href={`tel:${contact.phone}`} className="underline hover:text-amber-900">
                            {contact.firstName} {contact.lastName}
                            {contact.knowsUserPin && contact.knowsCompanyPin
                              ? " (knows user & company PIN)"
                              : contact.knowsUserPin
                              ? " (knows user PIN)"
                              : contact.companyName
                              ? ` (knows company PIN – ${contact.companyName})`
                              : " (knows company PIN)"}
                            {" — "}
                            {formatPhoneNumber(contact.phone)}
                          </a>
                        ) : (
                          <span>
                            {contact.firstName} {contact.lastName}
                            {contact.companyName ? ` (knows company PIN – ${contact.companyName})` : " (knows company PIN)"}
                          </span>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            )}
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              placeholder="PIN"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-lg tracking-widest mb-2"
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handlePinSubmit()}
            />
            {pinError && (
              <p className="text-sm text-red-600 mb-2">{pinError}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                onClick={closePinModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                onClick={handlePinSubmit}
                disabled={pinSubmitting}
              >
                {pinSubmitting ? "Checking…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className="w-full min-h-screen bg-cover flex flex-col justify-center items-center p-2 pt-16"
        style={{ backgroundImage: `url('/images/${isMobile ? "mobile-background.png" : "desktop-background.png"}')` }}
      >
        <div className="fixed top-2 right-2 z-50 flex flex-wrap justify-end gap-2 max-w-[calc(100%-1rem)]">
          {/* Premium Glowing language select */}
          <div className="relative inline-flex h-12 overflow-hidden rounded-lg p-[1.5px] focus-within:ring-2 focus-within:ring-blue-500">
            <span className="absolute inset-[-1000%] animate-[spin_4s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#3b82f6_0%,#8b5cf6_50%,#3b82f6_100%)]"></span>
            <span className="inline-flex h-full w-full items-center justify-center rounded-lg bg-slate-900 px-3 text-sm font-medium text-white backdrop-blur-3xl gap-1">
              <span role="img" aria-label="flag" className="text-base mr-1">
                {SUPPORTED_LANGUAGES.find(l => l.code === currentLanguage)?.flag || '🌐'}
              </span>
              <select
                value={currentLanguage}
                onChange={handleLanguageChange}
                className="bg-transparent text-white border-none cursor-pointer text-sm font-semibold pr-4 outline-none focus:ring-0 focus:outline-none"
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='white' viewBox='0 0 24 24'><path d='M7 10l5 5 5-5z'/></svg>")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right center',
                  backgroundSize: '12px',
                  paddingRight: '16px',
                }}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code} className="text-gray-950 bg-white">
                    {lang.name}
                  </option>
                ))}
              </select>
            </span>
          </div>

          <a
            href="https://admin.scanid365.com"
            target="_blank"
            rel="noopener noreferrer"
            className="relative inline-flex h-12 active:scale-95 transition overflow-hidden rounded-lg p-[1.5px] focus:outline-none"
          >
            <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#e7029a_0%,#f472b6_50%,#bd5fff_100%)]"></span>
            <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-lg bg-slate-950 px-7 text-sm font-medium text-white backdrop-blur-3xl gap-2 undefined">
              Login
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 448 512"
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M429.6 92.1c4.9-11.9 2.1-25.6-7-34.7s-22.8-11.9-34.7-7l-352 144c-14.2 5.8-22.2 20.8-19.3 35.8s16.1 25.8 31.4 25.8H224V432c0 15.3 10.8 28.4 25.8 31.4s30-5.1 35.8-19.3l144-352z"></path>
              </svg>
            </span>
          </a>
          <a
            href="https://www.scanid365.com"
            target="_blank"
            rel="noopener noreferrer"
            className="relative inline-flex h-12 active:scale-95 transition overflow-hidden rounded-lg p-[1.5px] focus:outline-none"
          >
            <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#e7029a_0%,#f472b6_50%,#bd5fff_100%)]"></span>
            <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-lg bg-[#284199] px-7 text-sm font-medium text-white backdrop-blur-3xl gap-2 undefined">
              Learn More
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 448 512"
                height="1em"
                width="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M429.6 92.1c4.9-11.9 2.1-25.6-7-34.7s-22.8-11.9-34.7-7l-352 144c-14.2 5.8-22.2 20.8-19.3 35.8s16.1 25.8 31.4 25.8H224V432c0 15.3 10.8 28.4 25.8 31.4s30-5.1 35.8-19.3l144-352z"></path>
              </svg>
            </span>
          </a>
        </div>
        <div className="relative bg-white bg-opacity-95 rounded-2xl w-full max-w-[520px] flex flex-col p-4 sm:px-8 py-10">
          {/* Main row: EMS Access title, Safety ID, Submit */}
          <div className="w-full flex flex-wrap items-end gap-3 mb-6">
            <h1 className="text-gray-800 text-2xl sm:text-3xl font-bold shrink-0">
              EMS Access
            </h1>
            <form onSubmit={handleRiderIdSubmit} className="flex flex-wrap items-end gap-2 flex-1 min-w-0">
              <label htmlFor="rider_id" className="sr-only">Safety ID</label>
              <input
                type="text"
                id="rider_id"
                className="bg-gray-100 border border-gray-300 text-gray-900 rounded-lg focus:ring-blue-500 focus:border-blue-500 flex-1 min-w-[100px] max-w-[140px] p-2 text-sm"
                placeholder="Safety ID"
                value={riderId}
                onChange={(e) => setRiderId(e.target.value.toUpperCase())}
                required
                disabled={loading}
              />
              <button
                type="submit"
                className="text-white bg-green-700 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-300 font-medium rounded-lg px-4 py-2 text-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || (showCaptcha && process.env.REACT_APP_CAPTCHA_SITE_KEY && !captchaVerified)}
              >
                Submit
              </button>
            </form>
          </div>

          {showCaptcha && (
            <div className="mb-4">
              {process.env.REACT_APP_CAPTCHA_SITE_KEY ? (
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                />
              ) : (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  Captcha not configured (REACT_APP_CAPTCHA_SITE_KEY missing in .env). You may continue.
                </p>
              )}
            </div>
          )}

          {supabaseConfigMissing ? (
            <div className="w-full text-left text-gray-700 space-y-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="font-semibold text-amber-900">Service unavailable</p>
              <p className="text-sm">This service is temporarily unavailable. Please try again later or contact support.</p>
            </div>
          ) : (
            <>
          {error && <p className="text-red-500 font-semibold mb-4">{error}</p>}

          {riderInfo && (
            <div className="w-full flex flex-col space-y-5">
              {/* Two columns: left avatar; right name+age then Gender, Height, Weight */}
              <div className="flex gap-4">
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-300 bg-gray-100 relative">
                    {riderInfo.imageUrl ? (
                      <>
                         <img
                          src={riderInfo.imageUrl}
                          alt=""
                          className="w-full h-full object-cover absolute inset-0"
                          onLoad={() => console.log("Profile image loaded successfully")}
                          onError={(e) => {
                            e.target.style.display = "none";
                            const fallback = e.target.nextElementSibling;
                            if (fallback) fallback.classList.remove("hidden");
                          }}
                        />
                        <div className="hidden absolute inset-0 bg-blue-200 flex justify-center items-center text-xl font-semibold text-gray-700 capitalize">
                          {riderInfo.firstName
                            ? `${riderInfo.firstName?.slice(0, 1)}${riderInfo.lastName?.slice(0, 1) || ""}`
                            : riderInfo.fullName?.slice(0, 1) || "?"}
                        </div>
                      </>
                    ) : (
                      <div className="bg-blue-200 w-full h-full flex justify-center items-center text-xl font-semibold text-gray-700 capitalize">
                        {riderInfo.firstName
                          ? `${riderInfo.firstName?.slice(0, 1)}${riderInfo.lastName?.slice(0, 1) || ""}`
                          : riderInfo.fullName?.slice(0, 1) || "?"}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0 space-y-1 text-sm text-gray-700">
                  <p className="text-lg text-gray-900">
                    <span className="font-bold">{riderInfo.fullName}</span>
                    {riderInfo.showDobToEMS && riderInfo.age != null && riderInfo.age !== "" && (
                      <span>, Age: {riderInfo.age}</span>
                    )}
                  </p>
                  {(riderInfo.showGenderToEMS && riderInfo.gender) && (
                    <p>Gender: {riderInfo.gender.charAt(0).toUpperCase() + riderInfo.gender.slice(1).toLowerCase()}</p>
                  )}
                  {(riderInfo.showHeightToEMS && (riderInfo.heightFeet != null || riderInfo.heightInches != null)) && (
                    <p>Height: {[riderInfo.heightFeet ? `${riderInfo.heightFeet} Feet` : null, riderInfo.heightInches != null ? `${riderInfo.heightInches} Inches` : null].filter(Boolean).join(" ")}</p>
                  )}
                  {(riderInfo.showWeightToEMS && riderInfo.weight != null && riderInfo.weight !== "") && (
                    <p>Weight: {riderInfo.weight} lbs</p>
                  )}
                </div>
              </div>

              {/* Notes to EMS — from payload (response.data.notesToEMS / notes_to_ems). Respect showNotesToEMSToEMS flag so unchecking "Show to EMS" hides it. */}
              {/* Always shown, so responders know there is nothing on file rather than wondering if it failed to load. */}
              <div className="border-2 border-yellow-400 rounded-lg bg-yellow-50 p-3">
                <h4 className="font-bold text-gray-900 text-sm mb-1">Message to EMS:</h4>
                {riderInfo.showNotesToEMSToEMS !== false && riderInfo.notesToEMS != null && String(riderInfo.notesToEMS).trim() !== "" ? (
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{riderInfo.notesToEMS}</p>
                ) : (
                  <p className="text-sm text-gray-500 italic">None on file</p>
                )}
              </div>

              {/* Location: What3Words */}
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">Location:</h4>
                <p className="text-xs text-gray-500 mb-2">Get your location to share with emergency services or contacts.</p>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <button
                    type="button"
                    className="bg-blue-600 text-white rounded-lg px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    onClick={getLocationAndConvert}
                    disabled={locationLoading}
                  >
                    {locationLoading ? (
                      <>
                        <PuffLoader color="white" size={16} />
                        Getting location…
                      </>
                    ) : (
                      <>Get Location</>
                    )}
                  </button>
                </div>
                {locationError && (
                  <p className="text-sm text-red-600 mb-2">{locationError}</p>
                )}
                {locationW3w && (
                  <>
                    <p className="text-sm text-gray-600 font-mono mb-1">
                      <a
                        href={`https://w3w.co/${locationW3w.replace(/^\/+/, "").trim()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {locationW3w}
                      </a>
                    </p>
                    <p className="text-xs text-gray-500">Click to open map. This is where you are right now.</p>
                    <p className="text-xs text-gray-500 mt-1">This location will be included when you tap <strong>Send Info by Text</strong> next to 911 or any emergency contact below.</p>
                  </>
                )}
              </div>

              {/* Custom Fields: only show when user has at least one company with custom fields */}
              {riderInfo.companiesWithCustomFields && riderInfo.companiesWithCustomFields.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Custom Fields</h4>
                  <div className="space-y-3">
                    {riderInfo.companiesWithCustomFields.map((company, idx) => (
                      <div key={company.companyId || idx}>
                        <p className="font-semibold text-gray-800 text-sm mb-1">{company.companyName}</p>
                        <ul className="space-y-1.5">
                          {company.customFields.map((field, fidx) => (
                            <li key={fidx} className="bg-gray-100 rounded px-3 py-2 text-sm text-gray-800">
                              {field.name}: {field.value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Emergency Contacts — 911 always at top, then account contacts */}
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Emergency Contacts</h4>
                <div className="space-y-2">
                  {/* 911 — always shown */}
                  <div className="flex items-center justify-between w-full rounded-lg px-4 py-3 bg-red-50 border border-red-300 text-sm text-red-900">
                    <span className="flex flex-col gap-0.5">
                      <span className="font-semibold">911</span>
                      <span className="text-xs text-red-700">Emergency Services</span>
                    </span>
                    <div className="flex gap-2 shrink-0">
                      <a
                        href="tel:911"
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-100 border border-red-300 text-red-800 rounded-lg hover:bg-red-200 text-xs font-medium"
                        aria-label="Call 911"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                        Call
                      </a>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-100 border border-red-300 text-red-800 rounded-lg hover:bg-red-200 text-xs font-medium"
                        aria-label="Send SMS to 911"
                        onClick={() => sendSms("911", buildDetailedSmsBody())}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                        Send Info by Text
                      </button>
                    </div>
                  </div>

                  {/* Account emergency contacts */}
                  {sortContactsPinKnowersFirst(riderInfo.emergencyContacts || []).map((contact, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between w-full rounded-lg px-4 py-3 text-sm ${contact.phone ? "bg-green-50 border border-green-300 text-green-900" : "bg-gray-50 border border-gray-200 text-gray-700"}`}
                    >
                      <span className="flex flex-col gap-0.5 min-w-0 mr-2">
                        <span className="font-medium truncate">
                          {contact.firstName} {contact.lastName}
                          {contact.relationship ? `, ${contact.relationship}` : ""}
                        </span>
                        {contact.knowsUserPin && (
                          <span className="text-xs font-medium text-amber-700">Knows User PIN</span>
                        )}
                        {contact.knowsCompanyPin && (
                          <span className="text-xs font-medium text-blue-700">
                            {contact.companyName ? `${contact.companyName}: ` : ""}Company Emergency Contact
                          </span>
                        )}
                      </span>
                      {contact.phone && (
                        <div className="flex gap-2 shrink-0">
                          <a
                            href={`tel:${contact.phone}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 border border-green-300 text-green-800 rounded-lg hover:bg-green-200 text-xs font-medium"
                            aria-label={`Call ${contact.firstName}`}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                            Call
                          </a>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 border border-green-300 text-green-800 rounded-lg hover:bg-green-200 text-xs font-medium"
                            aria-label={`Text ${contact.firstName}`}
                            onClick={() => sendSms(contact.phone, buildContactSmsBody())}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                            Text
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {docsNotesError && (
                <p className="text-sm text-red-600 mb-2">{docsNotesError}</p>
              )}
              {(riderInfo.hasDocuments || riderInfo.hasNotes) && (
                <div className="rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-gray-600 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                    </svg>
                    <span className="text-xs font-medium text-gray-600">PIN protected</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {riderInfo.hasDocuments && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 rounded-lg px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={openDocs}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" /></svg>
                        Docs
                      </button>
                    )}
                    {riderInfo.hasNotes && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 rounded-lg px-4 py-2 text-sm hover:bg-gray-100"
                        onClick={openNotes}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                        Notes
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* Full-screen tabbed Documents & Notes */}
      {viewMode === "docs-notes" && (
        <div className="fixed inset-0 z-[1003] bg-white flex flex-col">
          <div className="flex items-center gap-2 p-3 border-b border-gray-200 bg-gray-50 shrink-0">
            <button
              type="button"
              className="p-2 rounded-lg hover:bg-gray-200"
              onClick={closeDocsNotesView}
              aria-label="Back to EMS card"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <h2 className="text-lg font-bold text-gray-900">Documents & Notes</h2>
          </div>
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              className={`flex-1 py-3 text-sm font-medium ${activeTab === "documents" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
              onClick={() => setActiveTab("documents")}
            >
              Documents
            </button>
            <button
              type="button"
              className={`flex-1 py-3 text-sm font-medium ${activeTab === "notes" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
              onClick={() => setActiveTab("notes")}
            >
              Notes
            </button>
          </div>
          <div className="flex-1 overflow-auto p-3">
            {activeTab === "documents" && (
              documentsList.length === 0 ? (
                <p className="text-gray-500">No documents.</p>
              ) : (
                <ul className="space-y-2">
                  {documentsList.map((doc) => {
                    const title = doc.title || doc.originalName || doc.fileName || "Document";
                    const type = formatContentType(doc.contentType || doc.documentType);
                    const uploadDate = doc.uploadDate ? formatDate(doc.uploadDate.split("T")[0]) : "";
                    const viewUrl = getDocumentViewUrl(doc, apiBase);
                    const canView = !!viewUrl;
                    const isLoading = documentViewLoading?.id === doc.id;
                    return (
                      <li key={doc.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <button
                          type="button"
                          className="w-full text-left flex items-center justify-between gap-2"
                          onClick={() => canView && !isLoading && openDocumentViewer(doc)}
                          disabled={!canView || isLoading}
                        >
                          <span className="font-medium text-gray-900 truncate">{title}</span>
                          {canView && (
                            <span className="text-blue-600 shrink-0">{isLoading ? "Loading…" : "View"}</span>
                          )}
                        </button>
                        <p className="text-xs text-gray-500 mt-1">
                          {type ? `${type}${uploadDate ? " • " : ""}` : ""}
                          {uploadDate ? `Added ${uploadDate}` : ""}
                        </p>
                        {!canView && (
                          <p className="text-xs text-amber-600 mt-1">View not available.</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )
            )}
            {activeTab === "notes" && (
              notesList.length === 0 ? (
                <p className="text-gray-500">No notes.</p>
              ) : (
                <ul className="space-y-2">
                  {notesList.map((note) => {
                    const preview = (note.content || "").trim().split("\n")[0] || "";
                    return (
                      <li key={note.id}>
                        <button
                          type="button"
                          className="w-full text-left border border-gray-200 rounded-lg p-3 bg-gray-50 hover:bg-gray-100"
                          onClick={() => setSelectedNote(note)}
                        >
                          <span className="font-medium text-gray-900 block">{note.title || "Note"}</span>
                          {preview && (
                            <span className="text-sm text-gray-600 block mt-1 truncate">{preview}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )
            )}
          </div>
        </div>
      )}

      {/* In-app document viewer (view only, downloads disabled) */}
      {docViewerDoc && (
        <div
          className="fixed inset-0 z-[1004] flex items-center justify-center p-4 bg-black/70"
          onClick={(e) => e.target === e.currentTarget && setDocViewerDoc(null)}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-gray-200 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate pr-2">
                {docViewerDoc.title || docViewerDoc.originalName || docViewerDoc.fileName || "Document"}
              </h3>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                onClick={() => setDocViewerDoc(null)}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div
              className="flex-1 min-h-0 overflow-hidden flex items-center justify-center bg-gray-100 p-2"
              onContextMenu={(e) => e.preventDefault()}
              style={{ userSelect: "none" }}
            >
              {(() => {
                const url = getDocumentViewUrl(docViewerDoc, apiBase);
                const ct = (docViewerDoc.contentType || "").toLowerCase();
                if (!url) {
                  return <p className="text-gray-500">View not available.</p>;
                }
                const previewType = getPreviewType(ct);
                if (ct.includes("pdf")) {
                  return (
                    <iframe
                      title={docViewerDoc.title || "Document"}
                      src={url}
                      className="w-full h-full min-h-[60vh] rounded-lg border-0"
                    />
                  );
                }
                if (ct.startsWith("image/")) {
                  return (
                    <img
                      src={url}
                      alt={docViewerDoc.title || "Document"}
                      className="max-w-full max-h-[80vh] object-contain"
                    />
                  );
                }
                if (previewType) {
                  return <DocumentPreview url={url} type={previewType} title={docViewerDoc.title} />;
                }
                return (
                  <div className="text-center text-gray-600">
                    <p className="mb-2">Preview not available for this file type.</p>
                    <p className="text-sm">{docViewerDoc.contentType || "Unknown type"}</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Note detail modal (within tabbed view) */}
      {viewMode === "docs-notes" && selectedNote && (
        <div
          className="fixed inset-0 z-[1004] flex items-center justify-center p-4 bg-black/70"
          onClick={(e) => e.target === e.currentTarget && setSelectedNote(null)}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-gray-200 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate pr-2">{selectedNote.title || "Note"}</h3>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                onClick={() => setSelectedNote(null)}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {(selectedNote.content || "").trim() || "No content."}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EMSAccess;
